import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { UserService } from 'src/app/shared/services/user.service';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { BidAttachment, BidAttachmentGroup, BidDetails, BidEvaluationResult } from 'src/app/shared/models/app.models';

@Component({
  selector: 'app-bid-details',
  templateUrl: './bid-details.component.html',
  styleUrls: ['./bid-details.component.scss']
})

export class BidDetailsComponent implements OnInit {
  AppConstants = AppConstants;
  bidId: string = '';
  vendorId: string = '';
  bidDetails: BidDetails | null = null;
  bidErrorMessage: string = '';

  // Attachments
  bidAttachments: BidAttachment[] = [];
  bidAttachmentGroups: BidAttachmentGroup[] = [];
  evaluationResults: BidEvaluationResult[] = [];
  isLoadingAttachments: boolean = false;
  showAwardModal: boolean = false;
  awardLetterFile: File | null = null;
  awardRegretLetterFile: File | null = null;
  showRegretModal: boolean = false;
  regretLetterFile: File | null = null;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _loaderService: LoaderService,
    private _http: HttpClient,
    private _toasterService: ToasterService,
    private _cdr: ChangeDetectorRef,
    private _userService: UserService
  ) { }

  ngOnInit(): void {
    this._route.params.subscribe(params => {
      this.bidId = params['id'];
      this.vendorId = this._route.snapshot.queryParamMap.get('vendorId') || '';
      if (this.bidId) {
        this.fetchBidDetails();
      }
    });
  }

  private createApiService(): ApiService {
    const apiService = new ApiService(this._http);
    apiService['_baseUrl'] = environment.API_URL;
    return apiService;
  }

  async fetchBidDetails(): Promise<void> {
    this._loaderService.showLoader();
    this.bidErrorMessage = ''; // Clear any previous error messages
    try {
      const apiService = this.createApiService();
      const req$ = apiService.get(
        AppConstants.CONTROLLERS.bids,
        `${AppConstants.ACTIONS.GetBidDetailsByBidId}/${this.bidId}`,
        true,
        environment.APIs_PREFIX,
        this.vendorId ? { vendorId: this.vendorId } : undefined
      );

      const response: any = await lastValueFrom(req$ as any);
      if (response?.isSuccess === false) {
        this.bidErrorMessage = response?.errorMessageEn || response?.errorMessageAr || 'Failed to fetch bid details';
        this.bidDetails = null;
      } else {
        this.bidDetails = this.mapApiResponseToBidDetails(response?.data);
        const canOpenBid = await this.ensureBidIsUnsealed();
        if (!canOpenBid) {
          this._cdr.detectChanges();
          return;
        }
        this.bidErrorMessage = ''; // Clear error on success
      }
      this._cdr.detectChanges(); // Ensure view updates without extra user interaction

      await this.loadEvaluationResults();
      // Load attachments after fetching bid details
      await this.loadBidAttachments();
    } catch (error: any) {
      console.error('Failed to fetch bid details:', error);
      this.bidErrorMessage = error?.error?.errorMessageEn || error?.error?.errorMessageAr || error?.message || 'Failed to fetch bid details';
      this.bidDetails = null;
    } finally {
      this._loaderService.hideLoader();
    }
  }

  private isTechnicalOrCommercialBidDocument(doc: any): boolean {
    const attachmentFor = (doc?.attachmentFor || doc?.AttachmentFor || '').toString().trim().toLowerCase().replace(/\s+/g, '');
    return attachmentFor === 'commercialproposal' ||
      attachmentFor === 'commercial' ||
      attachmentFor === 'technicalproposal' ||
      attachmentFor === 'technical';
  }

  private isPracticeBid(): boolean {
    const bidId = (this.bidDetails?.bidId || this.bidId || '').toString().trim().toUpperCase();
    return bidId.startsWith('PRA-') && !bidId.startsWith('FWA-PRA-');
  }

  private isBidDocumentUnsealed(doc: any): boolean {
    const rawValue = doc?.isUnsealed ?? doc?.IsUnsealed ?? doc?.unsealed ?? doc?.Unsealed ?? doc?.documentUnsealed ?? doc?.DocumentUnsealed;
    if (typeof rawValue === 'boolean') {
      return rawValue;
    }

    const status = (rawValue ?? doc?.sealStatus ?? doc?.SealStatus ?? doc?.documentStatus ?? doc?.DocumentStatus ?? doc?.status ?? doc?.Status ?? '')
      .toString()
      .trim()
      .toLowerCase();

    return status === 'unsealed' || status === 'open' || status === 'opened';
  }

  private async getBidDocumentMetadata(): Promise<any[]> {
    if (!this.bidDetails?.bidId || !this.bidDetails?.vendorId) {
      return [];
    }

    const apiService = this.createApiService();
    const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
    const documentsDto = {
      applicationId: this.bidDetails.vendorId,
      processName: 'Bidding',
      processID: this.bidDetails.bidId,
    };

    const documentsResponse: any = await lastValueFrom(apiService.postByUrl(documentsUrl, documentsDto));
    return documentsResponse?.isSuccess && Array.isArray(documentsResponse?.data) ? documentsResponse.data : [];
  }

  private async ensureBidIsUnsealed(): Promise<boolean> {
    if (this.bidDetails?.isBidDecisionFinal || this.isPracticeBid() || this.isPaymentVerificationPending()) {
      return true;
    }

    if (this.bidDetails?.isBidLocked) {
      this.bidErrorMessage = this.bidDetails.sealedBidMessage || 'This bid can be opened after its proposal documents are unsealed or after a final award decision is recorded.';
      this.bidDetails = null;
      this.bidAttachments = [];
      return false;
    }

    try {
      const documents = await this.getBidDocumentMetadata();
      const proposalDocuments = documents.filter((doc: any) => this.isTechnicalOrCommercialBidDocument(doc));
      const isLocked = proposalDocuments.length > 0 && proposalDocuments.some((doc: any) => !this.isBidDocumentUnsealed(doc));

      if (isLocked) {
        this.bidErrorMessage = 'This bid is sealed. It can be opened after its technical and commercial proposal documents are unsealed.';
        this.bidDetails = null;
        this.bidAttachments = [];
        return false;
      }

      return true;
    } catch (error) {
      console.warn('[Bid Details] Failed to verify bid seal status:', error);
      this.bidErrorMessage = 'Unable to verify bid document seal status.';
      this.bidDetails = null;
      this.bidAttachments = [];
      return false;
    }
  }
  private mapApiResponseToBidDetails(data: any): BidDetails {
    if (!data) {
      return {
        bidId: this.bidId,
        tenderId: '',
        vendor: '',
        vendorId: '',
        contactPerson: '',
        contactPhone: '',
        contactEmail: '',
        submissionDate: '',
        bidValue: 0,
        deliveryPeriod: '',
        paymentTerms: '',
        warrantyPeriod: '',
        status: '',
        tcCommitteeStatus: '',
        awardDecision: '',
        evaluationType: '',
      };
    }

    const submissionDate = this.normalizeDate(
      data.bidSubmissionDate,
      data.submissionDeadline
    );

    return {
      bidId: data.bidId || this.bidId,
      tenderId: data.tenderId || '',
      vendor: data.vendorName || data.vendor || '',
      vendorId: data.vendorId,
      contactPerson: data.contactName || data.contactPerson || '',
      contactPhone: data.contactPhone || '',
      contactEmail: data.contactEmail || '',
      submissionDate,
      bidValue: data.bidValue ?? 0,
      deliveryPeriod: data.deliveryPeriodInDays ? `${data.deliveryPeriodInDays} days` : '',
      paymentTerms: data.paymentTerms || '',
      warrantyPeriod: data.warrantyPeriodInYears ? `${data.warrantyPeriodInYears} years` : '',
      status: (data.status || '').toString().toLowerCase() === 'regret' ? 'Rejected' : (data.status || ''),
      tcCommitteeStatus: (data.tcComitteeStatus ?? data.tCComitteeStatus ?? '').toString(),
      awardDecision: data.decision || data.awardDecision || '',
      evaluationType: data.evaluationType || '',
      isBidLocked: !!data.isBidLocked,
      isBidDecisionFinal: !!data.isBidDecisionFinal,
      sealedBidMessage: data.sealedBidMessage || '',
      joiningFee: data.joiningFee ?? null,
      paymentReferenceNumber: data.paymentReferenceNumber || '',
      accountNumber: data.accountNumber || '',
      bankName: data.bankName || '',
      paymentDate: data.paymentDate || null,
      joiningFeeDocuments: Array.isArray(data.joiningFeeDocuments) ? data.joiningFeeDocuments : []
      };
  }

  private formatFileSize(bytes: number): string {
    if (!bytes || isNaN(bytes)) {
      return '0 KB';
    }

    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  }

  private normalizeDate(primary?: string, fallback?: string): string {
    const isDefaultDate = (value?: string) => value && value.startsWith('0001-01-01');
    if (primary && !isDefaultDate(primary)) {
      return primary;
    }
    if (fallback && !isDefaultDate(fallback)) {
      return fallback;
    }
    return '';
  }

  async loadEvaluationResults(): Promise<void> {
    if (!this.bidDetails?.bidId) {
      this.evaluationResults = [];
      return;
    }

    try {
      const response: any = await lastValueFrom(this.createApiService().get(
        AppConstants.CONTROLLERS.bids,
        `GetBidEvaluationResults/${this.bidDetails.bidId}`,
        true,
        environment.APIs_PREFIX
      ));

      this.evaluationResults = response?.isSuccess && Array.isArray(response?.data)
        ? response.data.map((result: any) => ({
            evaluationId: result.evaluationId || '',
            evaluationType: result.evaluationType || '',
            evaluationStatus: result.evaluationStatus || '',
            decision: result.decision || 'Pending',
            comments: result.comments || '',
            rejectionReason: result.rejectionReason || '',
            decisionDate: result.decisionDate || null
          }))
        : [];
    } catch (error) {
      console.warn('[Bid Details] Failed to load evaluation results:', error);
      this.evaluationResults = [];
    }
  }

  getStatusColor(status: string): 'green' | 'blue' | 'pink' | 'gray' | 'yellow' | 'red' | 'orange' | 'purple' {
    const statusLower = status.toLowerCase();
    if (statusLower === 'under evaluation') return 'orange';
    if (statusLower === 'awarded') return 'green';
    if (statusLower === 'regret' || statusLower === 'regretted') return 'red';
    if (statusLower === 'pending' || statusLower === 'inprogress' || statusLower === 'in progress') return 'gray';
    if (statusLower === 'paymentverificationpending') return 'yellow';
    if (statusLower === 'submitted') return 'blue';
    return 'gray';
  }

  getAwardDecisionColor(decision: string): 'green' | 'blue' | 'pink' | 'gray' | 'yellow' | 'red' | 'orange' | 'purple' {
    const decisionLower = decision.toLowerCase();
    if (decisionLower === 'awarded') return 'green';
    if (decisionLower === 'regretted') return 'red';
    if (decisionLower === 'pending') return 'gray';
    return 'gray';
  }

  getEvaluationTypeColor(type: string): 'green' | 'blue' | 'pink' | 'gray' | 'yellow' | 'red' | 'orange' | 'purple' {
    const typeLower = type.toLowerCase();
    if (typeLower === 'both') return 'purple';
    if (typeLower === 'technical') return 'blue';
    if (typeLower === 'commercial') return 'green';
    return 'gray';
  }

  getEvaluationDecisionColor(decision: string): 'green' | 'blue' | 'pink' | 'gray' | 'yellow' | 'red' | 'orange' | 'purple' {
    const value = decision.toLowerCase();
    if (value === 'approved' || value === 'accepted') return 'green';
    if (value === 'rejected') return 'red';
    return 'gray';
  }

  canManageBidDecision(): boolean {
    const status = (this.bidDetails?.status || '').toLowerCase();
    const isActionable = status === 'submitted' || status === 'inprogress';
    return isActionable && (this.isCurrentUserSecretary()
      || (this.isCurrentUserProcurementOfficer() && this.isPracticeBid()));
  }

  isPaymentVerificationPending(): boolean {
    return (this.bidDetails?.status || '').toString().toLowerCase() === 'paymentverificationpending';
  }

  canVerifyJoiningFeePayment(): boolean {
    return this.isPaymentVerificationPending()
      && (this.isCurrentUserSecretary() || this.isCurrentUserProcurementOfficer());
  }

  async verifyJoiningFeePayment(): Promise<void> {
    if (!this.bidDetails?.bidId || !this.canVerifyJoiningFeePayment()) {
      return;
    }

    if (!window.confirm('Confirm that the joining-fee payment has been verified. The vendor will then be able to complete and submit this bid.')) {
      return;
    }

    try {
      this._loaderService.showLoader();
      const response: any = await lastValueFrom(this.createApiService().postByUrl(
        `${environment.API_URL}/api/Bids/${AppConstants.ACTIONS.VerifyJoiningFeePayment}/${this.bidDetails.bidId}`,
        {},
        this.vendorId ? { vendorId: this.vendorId } : undefined
      ));

      if (!response?.isSuccess && !response?.IsSuccess) {
        throw new Error(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to verify joining-fee payment.');
      }

      this._toasterService.showSuccess('Joining-fee payment verified. The bid is now available to the vendor as a draft.', 'Success');
      await this.fetchBidDetails();
    } catch (error: any) {
      this._toasterService.showError(
        error?.error?.errorMessageEn || error?.errorMessageEn || error?.message || 'Failed to verify joining-fee payment.',
        'Error'
      );
    } finally {
      this._loaderService.hideLoader();
    }
  }

  getAwardActionLabel(): string {
    return this.isCurrentUserProcurementOfficer() ? 'Accept' : 'Award';
  }

  private isCurrentUserSecretary(): boolean {
    const role = (this._userService.user?.Role || '').toString().trim().toLowerCase();
    return role === 'secretary' || role === 'tender committee secretary' || role === '4';
  }

  private isCurrentUserProcurementOfficer(): boolean {
    const role = (this._userService.user?.Role || '').toString().trim().toLowerCase();
    return role === 'procurementofficer' || role === 'procurement officer'
      || role === 'procurmentofficer' || role === 'procurment officer' || role === '7';
  }

  awardVendor(): void {
    if (!this.bidDetails?.bidId) return;
    this.awardLetterFile = null;
    this.awardRegretLetterFile = null;
    this.showAwardModal = true;
  }

  closeAwardModal(): void {
    this.showAwardModal = false;
    this.awardLetterFile = null;
    this.awardRegretLetterFile = null;
  }

  onAwardLetterSelected(event: Event): void {
    this.awardLetterFile = this.getSelectedPdf(event, 'award');
  }

  onAwardRegretLetterSelected(event: Event): void {
    this.awardRegretLetterFile = this.getSelectedPdf(event, 'regret');
  }

  private getSelectedPdf(event: Event, letterType: string): File | null {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return null;

    if (file.type !== 'application/pdf' || !file.name.toLowerCase().endsWith('.pdf')) {
      this._toasterService.showError(`Please upload a PDF ${letterType} letter.`, 'Validation Error');
      input.value = '';
      return null;
    }

    if (file.size > 5 * 1024 * 1024) {
      this._toasterService.showError(`The ${letterType} letter must not exceed 5 MB.`, 'Validation Error');
      input.value = '';
      return null;
    }

    return file;
  }

  async confirmAward(): Promise<void> {
    if (!this.bidDetails?.bidId || !this.awardLetterFile) {
      this._toasterService.showError('Please upload the award-letter PDF.', 'Validation Error');
      return;
    }

    try {
      this._loaderService.showLoader();
      const fileContent = await this.convertFileToBase64(this.awardLetterFile);
      const regretFileContent = this.awardRegretLetterFile
        ? await this.convertFileToBase64(this.awardRegretLetterFile)
        : null;
      const response: any = await lastValueFrom(this.createApiService().postByUrl(
        `${environment.API_URL}/api/Bids/AwardBid`,
        {
          bidId: this.bidDetails.bidId,
          fileContent: fileContent.includes(',') ? fileContent.split(',')[1] : fileContent,
          fileName: this.awardLetterFile.name,
          mimeType: 'application/pdf',
          regretFileContent: regretFileContent
            ? (regretFileContent.includes(',') ? regretFileContent.split(',')[1] : regretFileContent)
            : null,
          regretFileName: this.awardRegretLetterFile?.name || null,
          regretMimeType: this.awardRegretLetterFile ? 'application/pdf' : null
        }
      ));

      if (!response?.isSuccess && !response?.IsSuccess) {
        throw new Error(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to award vendor.');
      }

      this.closeAwardModal();
      this._toasterService.showSuccess('Vendor awarded successfully.', 'Success');
      await this.fetchBidDetails();
    } catch (error: any) {
      this._toasterService.showError(
        error?.error?.errorMessageEn || error?.errorMessageEn || error?.message || 'Failed to award vendor.',
        'Error'
      );
    } finally {
      this._loaderService.hideLoader();
    }
  }

  async regretVendor(): Promise<void> {
    if (!this.bidDetails?.bidId) return;
    this.regretLetterFile = null;
    this.showRegretModal = true;
  }

  closeRegretModal(): void {
    this.showRegretModal = false;
    this.regretLetterFile = null;
  }

  onRegretLetterSelected(event: Event): void {
    this.regretLetterFile = this.getSelectedPdf(event, 'regret');
  }

  async confirmRegret(): Promise<void> {
    if (!this.bidDetails?.bidId || !this.regretLetterFile) {
      this._toasterService.showError('Please upload the regret-letter PDF.', 'Validation Error');
      return;
    }

    try {
      this._loaderService.showLoader();
      const fileContent = await this.convertFileToBase64(this.regretLetterFile);
      const response: any = await lastValueFrom(this.createApiService().postByUrl(
        `${environment.API_URL}/api/Bids/RegretBid`,
        {
          bidId: this.bidDetails.bidId,
          fileContent: fileContent.includes(',') ? fileContent.split(',')[1] : fileContent,
          fileName: this.regretLetterFile.name,
          mimeType: 'application/pdf'
        }
      ));

      if (!response?.isSuccess && !response?.IsSuccess) {
        throw new Error(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to regret vendor.');
      }

      this._toasterService.showSuccess('Vendor regretted successfully.', 'Success');
      this.closeRegretModal();
      await this.fetchBidDetails();
    } catch (error: any) {
      this._toasterService.showError(
        error?.error?.errorMessageEn || error?.errorMessageEn || error?.message || 'Failed to regret vendor.',
        'Error'
      );
    } finally {
      this._loaderService.hideLoader();
    }
  }

  private convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  exportPDF(): void {
    // TODO: Implement PDF export functionality
    console.log('Exporting PDF for bid:', this.bidId);
    // Similar to other components, this would generate and download a PDF
  }

  goBack(): void {
    this._router.navigate([`/${AppConstants.ROUTES.BID}`]);
  }

  viewVendor(vendorId?: string): void {
    if (vendorId) {
      // TODO: Navigate to vendor details page if route exists
      console.log('View vendor:', vendorId);
    }
  }

  getBidErrorMessage(): string {
    return this.bidErrorMessage;
  }

  trackByAttachment(index: number, attachment: BidAttachment): string {
    return attachment.attachmentId || attachment.name || index.toString();
  }

  isAttachmentSealed(attachment: BidAttachment): boolean {
    if (this.isPracticeBid()) {
      return false;
    }

    const category = (attachment.category || '').toString().trim().toLowerCase().replace(/\s+/g, '');
    const isProposalDocument = category === 'technicalproposal' || category === 'commercialproposal';
    return isProposalDocument && attachment.isUnsealed === false;
  }

  canDownloadAttachment(attachment: BidAttachment): boolean {
    return !this.isAttachmentSealed(attachment) && !!(attachment.fileUrl || attachment.attachmentId);
  }

  async loadBidAttachments(): Promise<void> {
    console.log('Loading bid attachments for bid ID:', this.bidId);
    if (!this.bidId) {
      console.warn('[Bid Details] No bid ID available');
      return;
    }

    try {
      this.isLoadingAttachments = true;
      const apiService = this.createApiService();

      const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
      const documentsDto = {
        applicationId: this.bidDetails?.vendorId || '',
        processName: 'Bidding',
        processID: this.bidDetails?.bidId || '',
      };

      const documentsResponse: any = await lastValueFrom(apiService.postByUrl(documentsUrl, documentsDto));
      const documents = documentsResponse?.isSuccess && Array.isArray(documentsResponse?.data)
        ? documentsResponse.data
        : [];

      const joiningFeeDocuments = (this.bidDetails?.joiningFeeDocuments || [])
        .map((document: any) => ({ ...document, attachmentFor: 'Joining Fee Receipt' }));

      this.bidAttachments = documents
        .concat(joiningFeeDocuments)
        .filter((doc: any) => doc?.name || doc?.Name || doc?.attachmentId || doc?.AttachmentId)
        .map((doc: any) => this.mapDocumentToAttachment(doc));
      this.bidAttachmentGroups = this.groupBidAttachments(this.bidAttachments);
      this._cdr.detectChanges();
    } catch (error) {
      console.error('Error loading bid attachments:', error);
      this.bidAttachments = [];
      this.bidAttachmentGroups = [];
    } finally {
      this.isLoadingAttachments = false;
    }
  }

  private mapDocumentToAttachment(doc: any): BidAttachment {
    const size = Number(doc.fileSize ?? doc.FileSize ?? 0) || 0;
    return {
      category: this.getDocumentCategory(doc),
      name: doc.name || doc.Name || 'Unknown document',
      size,
      sizeFormatted: size > 0 ? this.formatFileSize(size) : '-',
      status: doc.isUnsealed || doc.IsUnsealed ? 'Unsealed' : 'Sealed',
      date: doc.createdAt || doc.CreatedAt || doc.uploadDate || doc.UploadDate || null,
      fileUrl: doc.fileContent || doc.FileContent || '',
      mimeType: doc.mimeType || doc.MimeType || 'application/octet-stream',
      attachmentId: doc.attachmentId || doc.AttachmentId,
      isUnsealed: !!(doc.isUnsealed ?? doc.IsUnsealed),
      documentExpiryDate: doc.documentExpiryDate || doc.DocumentExpiryDate || doc.expiryDate || doc.ExpiryDate || null
    };
  }

  private getDocumentCategory(doc: any): string {
    const rawCategory = (doc?.attachmentFor || doc?.AttachmentFor || 'Other documents').toString().trim();
    return rawCategory || 'Other documents';
  }

  private groupBidAttachments(attachments: BidAttachment[]): BidAttachmentGroup[] {
    const categoryOrder = [
      'Technical proposal',
      'Commercial proposal',
      'Tender bond',
      'Performance Bond',
      'Performance bond',
      'Other documents'
    ];

    const groups = attachments.reduce((acc: Map<string, BidAttachment[]>, attachment: BidAttachment) => {
      const category = attachment.category || 'Other documents';
      if (!acc.has(category)) {
        acc.set(category, []);
      }
      acc.get(category)!.push(attachment);
      return acc;
    }, new Map<string, BidAttachment[]>());

    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        const aIndex = categoryOrder.findIndex(x => x.toLowerCase() === a.toLowerCase());
        const bIndex = categoryOrder.findIndex(x => x.toLowerCase() === b.toLowerCase());
        const normalizedA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const normalizedB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        return normalizedA === normalizedB ? a.localeCompare(b) : normalizedA - normalizedB;
      })
      .map(([category, groupAttachments]) => ({ category, attachments: groupAttachments }));
  }
  async downloadAttachment(attachment: BidAttachment): Promise<void> {
    try {
      if (attachment.fileUrl) {
        // Direct download from URL
        const response = await fetch(attachment.fileUrl);
        if (!response.ok) {
          throw new Error('Failed to download file');
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else if (attachment.attachmentId) {
        // Download via backend
        const downloadUrl = `${environment.API_URL}/api/Documents/DownloadDocument/${attachment.attachmentId}`;
        window.open(downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  }
}

